import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Link,
  List,
  ListItem,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { Menu as MenuIcon, SearchIcon } from '~/Assets/icons'
import ContentfulImage from '~/components/shared-components/ContentfulImage/ContentfulImage'
import { useNavigation } from '~/context/NavigationProvider'
import { pxToRem } from '~/utils/pxToRem'

import { type IReducedAsset } from '../../../types/reducedTypes'
import Container from '../../grid/Container/Container'
import { type EntryId } from '../../shared-components/DocumentToReactRender/DocumentToReactRender'
import MainNav from '../MainNav/MainNav'
import TopNav from '../TopNav/TopNav'

interface NavItem {
  id: EntryId
  text: string
}

interface NavItemGroup {
  id: EntryId
  title: string
  links: NavItem[]
}

interface TopLevelNavItem extends NavItem {
  children: MainNavItem[]
}

interface MainNavItem extends NavItem {
  groups: NavItemGroup[]
}

export interface NavigationProps {
  logo?: IReducedAsset
  logoUrl: string
  loginText: string
  loginLinks: {
    id: EntryId
    url: string
    text: string
  }[]
  navItems: TopLevelNavItem[]
  isBusinessFinance?: boolean
}

export default function Navigation({
  navItems,
  logo,
  logoUrl,
  loginText,
  loginLinks,
  isBusinessFinance = false,
}: NavigationProps) {
  const { findFullUrlFromId } = useNavigation()

  const router = useRouter()
  const currentPath = router.asPath

  const mainNavItems =
    navItems.find(navItem => {
      return currentPath.startsWith(`/${findFullUrlFromId(navItem.id) ?? ''}`)
    })?.children ?? []

  const DesktopNav = () => (
    <nav className="navigation navigation-desktop" aria-label="primary">
      <TopNav
        navItems={navItems}
        loginLinks={loginLinks}
        loginText={loginText}
      />

      <MainNav
        navItems={mainNavItems}
        logoUrl={logoUrl}
        logo={logo}
        isBusinessFinance={isBusinessFinance}
      />
    </nav>
  )

  const MobileNav = () => {
    const { isOpen, onOpen, onClose } = useDisclosure()

    return (
      <nav className="navigation navigation-mobile" aria-label="primary">
        <Box boxShadow="0px 0px 16px rgba(0, 31, 38, 0.345497)" bg="white">
          <Container>
            <Flex align="center" p="10px 9px" justify="space-between">
              <Link as={NextLink} href={logoUrl} h="31px" py="5px">
                {logo && (
                  <ContentfulImage
                    image={logo}
                    overrideValues={{
                      width: 138,
                      height: 21,
                    }}
                    style={{
                      objectFit: 'contain',
                      width: 'auto',
                      height: '100%',
                    }}
                    loading="eager"
                    decoding="sync"
                    priority={true}
                  />
                )}
              </Link>

              <Button
                fontSize={pxToRem(24)}
                variant="unstyled"
                onClick={onOpen}
                ml="40px"
                _focusVisible={{
                  shadow: 'outline',
                  outline: '2px solid white',
                  outlineOffset: '2px',
                }}
              >
                <MenuIcon
                  role="button"
                  aria-label="Open the Cynergy Bank navigation menu, including Online Banking login"
                />
              </Button>

              <Drawer
                isOpen={isOpen}
                placement="right"
                onClose={onClose}
                size="md"
              >
                <DrawerOverlay bg="rgba(0, 0, 0, 0.75)" />
                <DrawerContent aria-label="Navigation Menu">
                  <DrawerCloseButton
                    p="12px"
                    width="40px"
                    height="40px"
                    fontSize={pxToRem(16)}
                    top="15px"
                    right="17px"
                    backgroundColor="primary.darkGreen.100"
                    zIndex="1"
                    sx={{ svg: { path: { fill: 'primary.white.100' } } }}
                    _focusVisible={{
                      shadow: 'outline',
                      outline: '2px solid white',
                      outlineOffset: '2px',
                    }}
                    aria-label="Close the Cynergy Bank navigation menu"
                  />

                  <Box
                    bg="primary.darkGreen.100"
                    p="60px 25px"
                    color="primary.white.100"
                    flex="1 1 0%"
                    overflow="auto"
                  >
                    <Accordion allowToggle variant="navStyles" as={List}>
                      <AccordionItem
                        as={ListItem}
                        borderColor="primary.white.100"
                      >
                        <AccordionButton
                          color="inherit"
                          _hover={{ bg: 'transparent' }}
                        >
                          {loginText}
                          <AccordionIcon />
                        </AccordionButton>

                        <AccordionPanel pb="24px">
                          <List spacing="24px">
                            {loginLinks?.map((link, index) => (
                              <ListItem key={`${link.id}-${index}`}>
                                <Link
                                  href={link.url ?? ''}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {link.text}
                                </Link>
                              </ListItem>
                            ))}
                          </List>
                        </AccordionPanel>
                      </AccordionItem>

                      {navItems.map((navItem, index) => {
                        return (
                          <AccordionItem
                            key={`${navItem.id}-${index}`}
                            borderColor="primary.white.100"
                            as={ListItem}
                          >
                            <AccordionButton
                              color="inherit"
                              _hover={{ bg: 'transparent' }}
                            >
                              {navItem.text}
                              <AccordionIcon />
                            </AccordionButton>

                            <AccordionPanel pb="24px">
                              <List spacing="24px">
                                {[
                                  navItem,
                                  ...navItem.children.reduce(
                                    (items, child) => [
                                      ...items,
                                      child,
                                      ...child.groups.flatMap(
                                        group => group.links,
                                      ),
                                    ],
                                    [] as NavItem[],
                                  ),
                                ].map((item, index) => (
                                  <ListItem key={`${item.id}-${index}`}>
                                    <Link
                                      as={NextLink}
                                      href={`/${findFullUrlFromId(item.id) ?? ''}`}
                                    >
                                      {item.text}
                                    </Link>
                                  </ListItem>
                                ))}
                              </List>
                            </AccordionPanel>
                          </AccordionItem>
                        )
                      })}
                    </Accordion>

                    <Link
                      href="/search-results"
                      py="20px"
                      display="flex"
                      justifyContent="space-between"
                      gap="20px"
                    >
                      <Text textTransform="uppercase" fontSize={pxToRem(20)}>
                        Search
                      </Text>
                      <SearchIcon boxSize="20px" />
                    </Link>
                  </Box>
                </DrawerContent>
              </Drawer>
            </Flex>
          </Container>
        </Box>
      </nav>
    )
  }

  // Render both navs, hide/show with CSS
  return (
    <>
      <div className="navigation-desktop-wrapper">
        <DesktopNav />
      </div>
      <div className="navigation-mobile-wrapper">
        <MobileNav />
      </div>
    </>
  )
}
